import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { submitHealthReport, getProjects, getHealthReports } from '../../api/client';

interface Project {
  ProjectId: string;
  Title: string;
  Description: string;
  WorkType: string;
  Domain: string;
  Status: string;
  RiskComplianceSecurityFlag: boolean;
  CreatedByEmail: string;
  CreatedByName: string;
  CreatedDate: string;
}

interface HealthReport {
  HealthReportId?: string;
  ProjectId: string;
  ProjectName?: string;
  ReportMonth: string;
  RAGStatus: 'Green' | 'Amber' | 'Red';
  Confidence: 'High' | 'Medium' | 'Low';
  ProgressNotes?: string;
  ActualHoursLogged: number;
  SubmittedDate?: string;
  PMEmail?: string;
}

export default function HealthReportPage() {
  const { user } = useAuth();

  // Form state
  const [projectId, setProjectId] = useState('');
  const [reportMonth, setReportMonth] = useState(new Date().toISOString().slice(0, 7));
  const [ragStatus, setRagStatus] = useState<'Green' | 'Amber' | 'Red'>('Green');
  const [confidence, setConfidence] = useState<'High' | 'Medium' | 'Low'>('High');
  const [progressNotes, setProgressNotes] = useState('');
  const [hoursLogged, setHoursLogged] = useState('0');

  // Data loading state
  const [projects, setProjects] = useState<Project[]>([]);
  const [healthReports, setHealthReports] = useState<HealthReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [projectsRes, reportsRes] = await Promise.all([
        getProjects(),
        getHealthReports(),
      ]);

      if (projectsRes.success) {
        setProjects(projectsRes.data || []);
      }
      if (reportsRes.success) {
        setHealthReports(reportsRes.data || []);
      }
    } catch (err: any) {
      console.error('Failed to load data:', err);
      setErrorMessage('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage('');
    setErrorMessage('');

    if (!projectId) {
      setErrorMessage('Please select a project');
      return;
    }

    setSubmitting(true);
    try {
      const userEmail = localStorage.getItem('auth_email') || user?.Email || 'pm@example.com';
      const userName = localStorage.getItem('auth_name') || user?.Name || 'Unknown User';

      const result = await submitHealthReport({
        projectId,
        reportMonth,
        ragStatus,
        confidence,
        progressNotes: progressNotes || undefined,
        actualHoursLogged: parseFloat(hoursLogged) || 0,
        pmEmail: userEmail,
        pmName: userName,
      });

      if (result.success) {
        setSuccessMessage('Health report submitted successfully!');
        // Reset form
        setProjectId('');
        setReportMonth(new Date().toISOString().slice(0, 7));
        setRagStatus('Green');
        setConfidence('High');
        setProgressNotes('');
        setHoursLogged('0');
        // Reload reports
        loadData();
      } else {
        setErrorMessage(result.error || 'Failed to submit report');
      }
    } catch (err: any) {
      setErrorMessage(err.message || 'Error submitting report');
    } finally {
      setSubmitting(false);
    }
  };

  const ragColors: Record<string, { bg: string; color: string }> = {
    Green: { bg: '#d4edda', color: '#155724' },
    Amber: { bg: '#fff3cd', color: '#856404' },
    Red: { bg: '#f8d7da', color: '#721c24' },
  };

  const confidenceColors: Record<string, string> = {
    High: '#06b6d4',
    Medium: '#ff9800',
    Low: '#ff6b6b',
  };

  if (loading) {
    return (
      <div className="container">
        <div style={{ padding: '2rem', textAlign: 'center', color: '#666' }}>
          Loading...
        </div>
      </div>
    );
  }

  return (
    <div className="container" style={{ maxWidth: '1200px' }}>
      {/* Header */}
      <header className="header" style={{ marginBottom: '2rem' }}>
        <h1 style={{ margin: '0 0 0.5rem 0' }}>Monthly Health Reports</h1>
        <p className="header-subtitle">
          Submit and track project health status updates with RAG indicators, confidence levels, and progress notes
        </p>
      </header>

      {/* Success/Error Messages */}
      {successMessage && (
        <div
          style={{
            padding: '1rem',
            marginBottom: '1.5rem',
            background: '#d4edda',
            color: '#155724',
            borderRadius: '8px',
            border: '1px solid #c3e6cb',
          }}
        >
          ✓ {successMessage}
        </div>
      )}

      {errorMessage && (
        <div
          style={{
            padding: '1rem',
            marginBottom: '1.5rem',
            background: '#f8d7da',
            color: '#721c24',
            borderRadius: '8px',
            border: '1px solid #f5c6cb',
          }}
        >
          ✗ {errorMessage}
        </div>
      )}

      {/* Main Content Grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '2rem', marginBottom: '2rem' }}>
        {/* Form */}
        <div>
          <div
            style={{
              background: 'white',
              padding: '2rem',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #e2e8f0',
            }}
          >
            <h2 style={{ marginTop: 0, marginBottom: '1.5rem', color: '#1a1f3a' }}>
              Submit Health Report
            </h2>

            <form onSubmit={handleSubmit}>
              {/* Project Selector */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#333' }}>
                  Project *
                </label>
                <select
                  value={projectId}
                  onChange={(e) => setProjectId(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontFamily: 'inherit',
                  }}
                >
                  <option value="">-- Select a project --</option>
                  {projects.map((p) => (
                    <option key={p.ProjectId} value={p.ProjectId}>
                      {p.Title} ({p.Domain})
                    </option>
                  ))}
                </select>
                <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                  Choose the project this report is for
                </p>
              </div>

              {/* Report Month */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#333' }}>
                  Report Month *
                </label>
                <input
                  type="month"
                  value={reportMonth}
                  onChange={(e) => setReportMonth(e.target.value)}
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                  }}
                />
                <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                  Month in YYYY-MM format
                </p>
              </div>

              {/* RAG Status */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: 600, color: '#333' }}>
                  RAG Status *
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
                  {(['Green', 'Amber', 'Red'] as const).map((status) => (
                    <label
                      key={status}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0.75rem',
                        border: ragStatus === status ? `2px solid ${ragColors[status].color}` : '1px solid #e2e8f0',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        background: ragStatus === status ? ragColors[status].bg : 'white',
                        transition: 'all 0.2s',
                      }}
                    >
                      <input
                        type="radio"
                        name="ragStatus"
                        value={status}
                        checked={ragStatus === status}
                        onChange={(e) => setRagStatus(e.target.value as 'Green' | 'Amber' | 'Red')}
                        style={{ marginRight: '0.5rem', cursor: 'pointer' }}
                      />
                      <span style={{ fontWeight: 600, color: ragColors[status].color }}>
                        {status}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Confidence */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.75rem', fontWeight: 600, color: '#333' }}>
                  Confidence Level *
                </label>
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '0.75rem' }}>
                  {(['High', 'Medium', 'Low'] as const).map((level) => (
                    <label
                      key={level}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0.75rem',
                        border: confidence === level ? `2px solid ${confidenceColors[level]}` : '1px solid #e2e8f0',
                        borderRadius: '8px',
                        cursor: 'pointer',
                        background: confidence === level ? `${confidenceColors[level]}20` : 'white',
                        transition: 'all 0.2s',
                      }}
                    >
                      <input
                        type="radio"
                        name="confidence"
                        value={level}
                        checked={confidence === level}
                        onChange={(e) => setConfidence(e.target.value as 'High' | 'Medium' | 'Low')}
                        style={{ marginRight: '0.5rem', cursor: 'pointer' }}
                      />
                      <span style={{ fontWeight: 600, color: confidenceColors[level] }}>
                        {level}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Progress Notes */}
              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#333' }}>
                  Progress Notes
                </label>
                <textarea
                  value={progressNotes}
                  onChange={(e) => setProgressNotes(e.target.value)}
                  placeholder="What happened this month? Any blockers, risks, or achievements?"
                  rows={4}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                    fontFamily: 'inherit',
                    resize: 'vertical',
                  }}
                />
              </div>

              {/* Actual Hours Logged */}
              <div style={{ marginBottom: '2rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, color: '#333' }}>
                  Actual Hours Logged *
                </label>
                <input
                  type="number"
                  value={hoursLogged}
                  onChange={(e) => setHoursLogged(e.target.value)}
                  placeholder="0"
                  step="0.5"
                  min="0"
                  required
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    border: '1px solid #e2e8f0',
                    borderRadius: '8px',
                    fontSize: '1rem',
                  }}
                />
                <p style={{ fontSize: '0.85rem', color: '#666', marginTop: '0.25rem' }}>
                  Total hours logged for this period
                </p>
              </div>

              {/* Submit Button */}
              <button
                type="submit"
                disabled={submitting}
                style={{
                  width: '100%',
                  padding: '0.75rem 1.5rem',
                  background: submitting ? '#999' : '#06b6d4',
                  color: 'white',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: submitting ? 'not-allowed' : 'pointer',
                  fontWeight: 600,
                  fontSize: '1rem',
                  transition: 'all 0.2s',
                  opacity: submitting ? 0.7 : 1,
                }}
              >
                {submitting ? '⏳ Submitting...' : '✓ Submit Health Report'}
              </button>
            </form>
          </div>
        </div>

        {/* Stats Panel */}
        <div>
          <div
            style={{
              background: 'white',
              padding: '2rem',
              borderRadius: '12px',
              boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
              border: '1px solid #e2e8f0',
            }}
          >
            <h2 style={{ marginTop: 0, marginBottom: '1.5rem', color: '#1a1f3a' }}>
              Overview
            </h2>

            {/* Stats Grid */}
            <div style={{ display: 'grid', gap: '1rem', marginBottom: '2rem' }}>
              <div
                style={{
                  padding: '1rem',
                  background: '#f0f9ff',
                  borderRadius: '8px',
                  borderLeft: '4px solid #06b6d4',
                }}
              >
                <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.5rem' }}>
                  Total Reports
                </div>
                <div style={{ fontSize: '2rem', fontWeight: 700, color: '#06b6d4' }}>
                  {healthReports.length}
                </div>
              </div>

              {/* RAG Distribution */}
              <div style={{ marginTop: '1rem' }}>
                <h3 style={{ margin: '0 0 1rem 0', fontSize: '1rem', color: '#333', fontWeight: 600 }}>
                  Latest Status Distribution
                </h3>
                {['Green', 'Amber', 'Red'].map((status) => {
                  const count = healthReports.filter((r) => r.RAGStatus === status).length;
                  return (
                    <div
                      key={status}
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        padding: '0.75rem',
                        marginBottom: '0.5rem',
                        background: ragColors[status].bg,
                        borderRadius: '6px',
                      }}
                    >
                      <span style={{ fontWeight: 600, color: ragColors[status].color }}>
                        {status}
                      </span>
                      <span
                        style={{
                          fontSize: '1.5rem',
                          fontWeight: 700,
                          color: ragColors[status].color,
                        }}
                      >
                        {count}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Instructions */}
            <div
              style={{
                padding: '1rem',
                background: '#f8fafc',
                borderRadius: '8px',
                borderLeft: '4px solid #06b6d4',
                fontSize: '0.9rem',
                color: '#333',
                lineHeight: '1.6',
              }}
            >
              <h3 style={{ margin: '0 0 0.75rem 0', color: '#1a1f3a', fontSize: '0.95rem' }}>
                How to Submit
              </h3>
              <ol style={{ margin: 0, paddingLeft: '1.25rem' }}>
                <li>Select a project from the dropdown</li>
                <li>Choose the report month</li>
                <li>Set the RAG status (Green/Amber/Red)</li>
                <li>Indicate your confidence level</li>
                <li>Add progress notes (optional)</li>
                <li>Enter actual hours logged</li>
                <li>Click Submit</li>
              </ol>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Reports */}
      {healthReports.length > 0 && (
        <div style={{ marginTop: '3rem' }}>
          <h2 style={{ marginBottom: '1.5rem', color: '#1a1f3a', fontSize: '1.5rem' }}>
            Recent Health Reports
          </h2>

          <div
            style={{
              display: 'grid',
              gap: '1rem',
            }}
          >
            {healthReports.slice(0, 10).map((report) => (
              <div
                key={report.HealthReportId || `${report.ProjectId}-${report.ReportMonth}`}
                style={{
                  background: 'white',
                  padding: '1.5rem',
                  borderRadius: '12px',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                  border: '1px solid #e2e8f0',
                }}
              >
                <div style={{ display: 'grid', gridTemplateColumns: '1fr auto auto auto', gap: '2rem', marginBottom: '1rem' }}>
                  <div>
                    <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.25rem' }}>
                      Project
                    </div>
                    <div style={{ fontWeight: 600, color: '#1a1f3a', fontSize: '1.1rem' }}>
                      {report.ProjectName || 'Unknown'}
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.25rem' }}>
                      Month
                    </div>
                    <div style={{ fontWeight: 600, color: '#1a1f3a' }}>
                      {report.ReportMonth}
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.25rem' }}>
                      Status
                    </div>
                    <div
                      style={{
                        display: 'inline-block',
                        padding: '0.5rem 1rem',
                        background: ragColors[report.RAGStatus].bg,
                        color: ragColors[report.RAGStatus].color,
                        borderRadius: '6px',
                        fontWeight: 600,
                        fontSize: '0.95rem',
                      }}
                    >
                      {report.RAGStatus}
                    </div>
                  </div>

                  <div>
                    <div style={{ fontSize: '0.9rem', color: '#666', marginBottom: '0.25rem' }}>
                      Confidence
                    </div>
                    <div
                      style={{
                        display: 'inline-block',
                        padding: '0.5rem 1rem',
                        background: `${confidenceColors[report.Confidence]}20`,
                        color: confidenceColors[report.Confidence],
                        borderRadius: '6px',
                        fontWeight: 600,
                        fontSize: '0.95rem',
                      }}
                    >
                      {report.Confidence}
                    </div>
                  </div>
                </div>

                {/* Details */}
                <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr 1fr', gap: '2rem', paddingTop: '1rem', borderTop: '1px solid #e2e8f0' }}>
                  {report.ProgressNotes && (
                    <div>
                      <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem', fontWeight: 600 }}>
                        Progress Notes
                      </div>
                      <div style={{ color: '#333', fontSize: '0.95rem', lineHeight: '1.5' }}>
                        {report.ProgressNotes}
                      </div>
                    </div>
                  )}

                  <div>
                    <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem', fontWeight: 600 }}>
                      Hours Logged
                    </div>
                    <div style={{ color: '#06b6d4', fontSize: '1.2rem', fontWeight: 700 }}>
                      {report.ActualHoursLogged}h
                    </div>
                  </div>

                  {report.SubmittedDate && (
                    <div>
                      <div style={{ fontSize: '0.85rem', color: '#666', marginBottom: '0.25rem', fontWeight: 600 }}>
                        Submitted
                      </div>
                      <div style={{ color: '#666', fontSize: '0.9rem' }}>
                        {new Date(report.SubmittedDate).toLocaleDateString()}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {healthReports.length > 10 && (
            <div style={{ textAlign: 'center', marginTop: '1.5rem', color: '#666' }}>
              Showing {10} of {healthReports.length} reports
            </div>
          )}
        </div>
      )}

      {healthReports.length === 0 && (
        <div
          style={{
            marginTop: '2rem',
            padding: '3rem',
            textAlign: 'center',
            background: 'white',
            borderRadius: '12px',
            border: '1px solid #e2e8f0',
            color: '#666',
          }}
        >
          <div style={{ fontSize: '1.2rem', marginBottom: '0.5rem' }}>
            No health reports submitted yet
          </div>
          <div style={{ fontSize: '0.95rem' }}>
            Submit your first monthly health report using the form above
          </div>
        </div>
      )}
    </div>
  );
}
